import React, { useState, useEffect } from 'react';
import { bool, func, string, arrayOf, shape, object } from 'prop-types';
import { connect } from 'react-redux';
import { propTypes } from '../../util/types';
import { useIntl } from '../../util/reactIntl';
import classNames from 'classnames';

import IconHeart from '../IconHeart/IconHeart';
import AddToWishlistModal from '../AddToWishlistModal/AddToWishlistModal';
import LoginModal from '../LoginModal/LoginModal';

import {
  getAllWishlists,
  getWishlistIdsForListing,
  addToWishlist,
  removeFromWishlist,
  createWishlist,
  loadData,
} from '../../ducks/wishlists.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

import css from './WishlistButton.module.css';

const WishlistButtonComponent = props => {
  const intl = useIntl();
  const {
    listingId,
    position,
    currentUser,
    isOwnListing,
    wishlists,
    wishlistIdsForListing,
    onAddToWishlist,
    onRemoveFromWishlist,
    onCreateWishlist,
    onLoadWishlists,
    onManageDisableScrolling,
    className,
    rootClassName,
  } = props;

  // Don't show wishlist button on own listings
  if (isOwnListing) {
    return null;
  }

  // The Modal renders its children even while closed - it only hides them with CSS -
  // so keeping it mounted would add a hidden <h2> title to the page for every card.
  // Mount it on first use instead, one render before opening it, so that Modal still
  // sees the closed -> open transition it needs to move focus into the dialog.
  const [isModalMounted, setIsModalMounted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoginModalMounted, setIsLoginModalMounted] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [wishlistsLoaded, setWishlistsLoaded] = useState(false);

  const isAuthenticated = !!currentUser?.id?.uuid;

  const listingIdString = typeof listingId === 'string' ? listingId : listingId?.uuid;

  // Check if listing is in any wishlist
  useEffect(() => {
    if (currentUser?.id?.uuid) {
      const wishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists;

      // Handle both old format (array) and new format (object)
      let inWishlist = false;
      if (Array.isArray(wishlistsData)) {
        // Old format: simple array of listing IDs
        inWishlist = wishlistsData.includes(listingIdString);
      } else if (wishlistsData && typeof wishlistsData === 'object') {
        // New format: object with wishlist IDs as keys
        inWishlist = Object.values(wishlistsData).some(wishlist =>
          wishlist.items && wishlist.items.some(item => item.listingId === listingIdString)
        );
      }

      setIsInWishlist(inWishlist);
    }
  }, [currentUser?.id?.uuid, listingIdString, currentUser?.attributes?.profile?.privateData?.wishlists]);

  // Also check from Redux state
  useEffect(() => {
    if (wishlistIdsForListing && wishlistIdsForListing.length > 0) {
      setIsInWishlist(true);
    }
  }, [wishlistIdsForListing]);

  useEffect(() => {
    if (isModalMounted) {
      setIsModalOpen(true);
    }
  }, [isModalMounted]);

  useEffect(() => {
    if (isLoginModalMounted) {
      setIsLoginModalOpen(true);
    }
  }, [isLoginModalMounted]);

  const openWishlistFlow = () => {
    // Read privateData: the wishlists prop stays empty until the picker has loaded it once
    const wishlistsData = currentUser?.attributes?.profile?.privateData?.wishlists;
    const wishlistIds = Array.isArray(wishlistsData) ? [] : Object.keys(wishlistsData || {});
    if (wishlistIds.length === 1 && !isInWishlist) {
      handleAddToWishlist(wishlistIds[0], listingIdString);
      return;
    }

    // Load wishlists data if not already loaded
    if (!wishlistsLoaded && wishlists.length === 0) {
      onLoadWishlists({});
      setWishlistsLoaded(true);
    }

    // Mount the modal; the effect above opens it on the next render
    setIsModalMounted(true);
  };

  // When login succeeds while the login modal is up, continue into the wishlist flow
  useEffect(() => {
    if (isAuthenticated && isLoginModalMounted) {
      setIsLoginModalOpen(false);
      setIsLoginModalMounted(false);
      openWishlistFlow();
    }
  }, [isAuthenticated]);

  const handleClick = e => {
    e.stopPropagation();
    e.preventDefault();

    // Ask for login in a modal if not authenticated
    if (!isAuthenticated) {
      setIsLoginModalMounted(true);
      return;
    }

    openWishlistFlow();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsModalMounted(false);
  };

  const handleCloseLoginModal = () => {
    setIsLoginModalOpen(false);
    setIsLoginModalMounted(false);
  };

  const handleAddToWishlist = async (wishlistId, listingIdParam) => {
    setIsUpdating(true);
    try {
      await onAddToWishlist(wishlistId, listingIdParam);
      setIsInWishlist(true);
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveFromWishlist = async (wishlistId, listingIdParam) => {
    setIsUpdating(true);
    try {
      await onRemoveFromWishlist(wishlistId, listingIdParam);
      // Check if listing is still in any other wishlist
      const remainingWishlists = wishlistIdsForListing.filter(id => id !== wishlistId);
      if (remainingWishlists.length === 0) {
        setIsInWishlist(false);
      }
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateWishlist = async (wishlistData) => {
    try {
      const newWishlist = await onCreateWishlist(wishlistData);
      return newWishlist;
    } catch (error) {
      console.error('Failed to create wishlist:', error);
      throw error;
    }
  };

  // Handle manage disable scrolling for modal
  const handleManageDisableScrolling = (componentId, disableScrolling) => {
    if (onManageDisableScrolling) {
      onManageDisableScrolling(componentId, disableScrolling);
    }
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.positionLeft]: position === 'left',
    [css.positionRight]: position === 'right',
  });

  const iconClasses = classNames(css.icon, {
    [css.iconInWishlist]: isInWishlist,
    [css.iconNotInWishlist]: !isInWishlist,
    [css.iconUpdating]: isUpdating,
  });

  return (
    <>
      <button
        type="button"
        className={classes}
        onClick={handleClick}
        disabled={isUpdating}
        aria-label={intl.formatMessage({
          id: isInWishlist ? 'WishlistButton.edit' : 'WishlistButton.add',
        })}
      >
        <IconHeart className={iconClasses} isFilled={isInWishlist} />
      </button>

      {isLoginModalMounted ? (
        <LoginModal
          id={`wishlist-login-modal-${listingIdString}`}
          isOpen={isLoginModalOpen}
          onClose={handleCloseLoginModal}
          onManageDisableScrolling={handleManageDisableScrolling}
        />
      ) : null}

      {isModalMounted ? (
        <AddToWishlistModal
          id={`wishlist-modal-${listingIdString}`}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onManageDisableScrolling={handleManageDisableScrolling}
          listingId={listingIdString}
          wishlists={wishlists}
          wishlistIdsForListing={wishlistIdsForListing}
          onAddToWishlist={handleAddToWishlist}
          onRemoveFromWishlist={handleRemoveFromWishlist}
          onCreateWishlist={handleCreateWishlist}
          isUpdating={isUpdating}
        />
      ) : null}
    </>
  );
};

WishlistButtonComponent.defaultProps = {
  position: 'right',
  className: null,
  rootClassName: null,
  isOwnListing: false,
  wishlists: [],
  wishlistIdsForListing: [],
};

WishlistButtonComponent.propTypes = {
  listingId: propTypes.uuid.isRequired,
  position: string,
  currentUser: propTypes.currentUser,
  isOwnListing: bool,
  className: string,
  rootClassName: string,
  wishlists: arrayOf(
    shape({
      id: string.isRequired,
      title: string.isRequired,
      items: arrayOf(object),
    })
  ),
  wishlistIdsForListing: arrayOf(string),
  onAddToWishlist: func.isRequired,
  onRemoveFromWishlist: func.isRequired,
  onCreateWishlist: func.isRequired,
  onLoadWishlists: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { currentUser } = state.user;
  const listingIdString = typeof ownProps.listingId === 'string'
    ? ownProps.listingId
    : ownProps.listingId?.uuid;

  return {
    currentUser,
    wishlists: getAllWishlists(state),
    wishlistIdsForListing: getWishlistIdsForListing(state, listingIdString),
  };
};

const mapDispatchToProps = dispatch => ({
  onAddToWishlist: (wishlistId, listingId) => dispatch(addToWishlist(wishlistId, listingId)),
  onRemoveFromWishlist: (wishlistId, listingId) => dispatch(removeFromWishlist(wishlistId, listingId)),
  onCreateWishlist: (wishlistData) => dispatch(createWishlist(wishlistData)),
  onLoadWishlists: (config) => dispatch(loadData(config)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const WishlistButton = connect(mapStateToProps, mapDispatchToProps)(WishlistButtonComponent);

export default WishlistButton;
